import { useCallback, useContext, useRef } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { Action, Interaction, trackingApiRef } from '../../apis';
import { TrackingContext } from '../../contexts';
import { ElementId, TrackingData } from '../../types';
import { parseElementId } from '../../utils';
import { usePageView } from '../usePageView';

interface TrackOption {
  elementId?: ElementId;
  type?: Interaction['type'];
  action?: Action;
  data?: TrackingData;
}

export interface UseInteractionTrackerOptions {
  elementId?: ElementId;
  type?: Interaction['type'];
  action?: Action;
  data?: TrackingData;
}

export function useInteractionTracker(options: TrackOption = {}) {
  const tracking = useApi(trackingApiRef);
  const pageView = usePageView();
  const warnedRef = useRef(false);
  const { path: parentPath, data: contextData } = useContext(TrackingContext);

  const track = useCallback(
    (interaction: TrackOption) => {
      const {
        elementId = options.elementId,
        type = options.type ?? 'click',
        action = options.action,
      } = interaction;

      if (!elementId) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            'No elementId provided to useInteractionTracker. This will not be tracked.',
          );
        }
        return;
      }

      if (!action) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            'No action provided to useInteractionTracker. This will not be tracked.',
          );
        }
        return;
      }

      const { role, name } = parseElementId(elementId);
      const elementPath = [...parentPath, { role, name }];
      const data = {
        ...contextData,
        ...options.data,
        ...interaction.data,
      };

      if (process.env.NODE_ENV === 'development' && !warnedRef.current) {
        if (pageView.pageId === null) {
          // eslint-disable-next-line no-console
          console.warn(
            `useInteractionTracker() fired outside a <PageImpression>. ` +
              'Events will not have a pageViewId.',
          );
          warnedRef.current = true;
        }
      }

      tracking.trackInteraction({
        elementPath,
        type,
        action,
        data,
      });
    },
    [
      options.elementId,
      options.data,
      options.action,
      options.type,
      parentPath,
      contextData,
      tracking,
      pageView.pageId,
    ],
  );

  return track;
}
